'use client';
import React from 'react';
import { motion } from 'framer-motion';
import { profile } from '@/data/profile';
import {
  FaGithub,
  FaLinkedin,
  FaKaggle,
  FaWhatsapp,
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaReddit,
  FaYoutube,
  FaStackOverflow,
  FaGlobe
} from 'react-icons/fa';
import { SiUpwork, SiFreelancer } from 'react-icons/si';
import OrbitalObject from '../ui/OrbitalObject';

export default function Contact() {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Create 20 orbital objects with varying properties
  const orbitalConfigs = Array.from({ length: 20 }, (_, index) => ({
    color: [
      "#4f83ff", "#83ff4f", "#ff4f83", "#ff834f", "#834fff",
      "#4fff83", "#ff4fff", "#ffff4f", "#4fffff", "#ffffff",
      "#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57",
      "#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3", "#ff9f43"
    ][index % 20],
    size: 4 + (index % 8) + 2, // Sizes from 6 to 13
    orbitDuration: 15 + (index % 25) + 5, // Duration from 20 to 44 seconds
    orbitRadius: 80 + (index % 15) * 20, // Radius from 80 to 360
    delay: (index % 10) * 2, // Delays from 0 to 18 seconds
  }));

  const primaryContacts = [
    {
      icon: <FaEnvelope className="w-6 h-6" />,
      label: 'Email',
      value: profile.contact.email,
      href: `mailto:${profile.contact.email}`,
      color: 'hover:text-red-400',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      icon: <FaWhatsapp className="w-6 h-6" />,
      label: 'WhatsApp',
      value: profile.contact.whatsapp,
      href: `https://wa.me/${profile.contact.whatsapp.replace(/[^0-9]/g, '')}`,
      color: 'hover:text-green-400',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: <FaLinkedin className="w-6 h-6" />,
      label: 'LinkedIn',
      value: 'ateebnoone',
      href: profile.contact.linkedin,
      color: 'hover:text-blue-400',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <FaGithub className="w-6 h-6" />,
      label: 'GitHub',
      value: '@AteebNoOne',
      href: profile.contact.github,
      color: 'hover:text-white',
      gradient: 'from-gray-600 to-gray-800'
    }
  ];

  const socialContacts = [
    {
      icon: <FaFacebook className="w-5 h-5" />,
      label: 'Facebook',
      href: profile.contact.facebook,
      color: 'hover:text-blue-500'
    },
    {
      icon: <FaInstagram className="w-5 h-5" />,
      label: 'Instagram',
      href: profile.contact.instagram,
      color: 'hover:text-pink-400'
    },
    {
      icon: <FaTwitter className="w-5 h-5" />,
      label: 'Twitter',
      href: profile.contact.twitter,
      color: 'hover:text-blue-400'
    },
    {
      icon: <FaReddit className="w-5 h-5" />,
      label: 'Reddit',
      href: profile.contact.reddit,
      color: 'hover:text-orange-400'
    },
    {
      icon: <FaYoutube className="w-5 h-5" />,
      label: 'YouTube',
      href: profile.contact.youtube,
      color: 'hover:text-red-500'
    },
    {
      icon: <FaKaggle className="w-5 h-5" />,
      label: 'Kaggle',
      href: profile.contact.kaggle,
      color: 'hover:text-blue-300'
    }
  ];

  const professionalContacts = [
    {
      icon: <FaStackOverflow className="w-5 h-5" />,
      label: 'Stack Overflow',
      href: profile.contact.stackoverflow,
      color: 'hover:text-orange-500'
    },
    {
      icon: <FaGlobe className="w-5 h-5" />,
      label: 'Website',
      href: profile.contact.website,
      color: 'hover:text-purple-400'
    },
    {
      icon: <SiFreelancer className="w-5 h-5" />,
      label: 'Freelancer',
      href: profile.contact.freelancer,
      color: 'hover:text-blue-500'
    },
    {
      icon: <SiUpwork className="w-5 h-5" />,
      label: 'Upwork',
      href: profile.contact.upwork,
      color: 'hover:text-green-500'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-black relative overflow-hidden">
      {/* Map through 20 orbital objects */}
      {orbitalConfigs.map((config, index) => (
        <div key={index} className="absolute inset-0" style={{ left: '50%', top: '50%' }}>
          <OrbitalObject
            color={config.color}
            size={config.size}
            orbitDuration={config.orbitDuration}
            orbitRadius={config.orbitRadius}
            delay={config.delay}
          />
        </div>
      ))}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4 text-white">Get in Touch</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Let&apos;s connect and build something amazing together
          </p>
        </motion.div>

        {/* Primary Contact Methods */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-semibold text-white text-center mb-8">Primary Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {primaryContacts.map((contact, index) => (
              <motion.a
                key={index}
                href={contact.href}
                target="_blank"
                rel="noopener noreferrer"
                variants={item}
                className={`relative flex flex-col items-center p-6 bg-zinc-800/80 backdrop-blur-sm 
                  text-gray-300 rounded-xl shadow-lg hover:shadow-[0_0_30px_rgba(79,131,255,0.2)] 
                  transition-all duration-300 border border-zinc-700/50 hover:border-blue-500/40 
                  group overflow-hidden ${contact.color}`}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                {/* Gradient background overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${contact.gradient} 
                  opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

                <div className="relative z-10 flex flex-col items-center">
                  <div className="mb-4 p-3 bg-zinc-700/50 rounded-full group-hover:bg-zinc-600/50 
                    transition-colors transform group-hover:scale-110" aria-hidden="true">
                    {React.cloneElement(contact.icon, { 'aria-hidden': 'true' })}
                  </div>
                  <div className="text-sm text-gray-400 mb-1">{contact.label}</div>
                  <div className="font-medium text-center group-hover:text-blue-300 transition-colors">
                    {contact.value}
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Social Media */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-semibold text-white text-center mb-8">Social Media</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {socialContacts.map((contact, index) => (
              <motion.a
                key={index}
                href={contact.href}
                target="_blank"
                rel="noopener noreferrer"
                variants={item}
                className={`flex flex-col items-center p-4 bg-zinc-800/60 backdrop-blur-sm 
                  text-gray-400 rounded-lg hover:bg-zinc-700/60 transition-all duration-300 
                  border border-zinc-700/30 hover:border-zinc-600/50 group ${contact.color}`}
                whileHover={{ scale: 1.1, y: -3 }}
              >
                <div className="mb-2 transform group-hover:scale-125 transition-transform" aria-hidden="true">
                  {React.cloneElement(contact.icon, { 'aria-hidden': 'true' })}
                </div>
                <span className="text-xs font-medium">{contact.label}</span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Professional Platforms */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-semibold text-white text-center mb-8">Professional Platforms</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {professionalContacts.map((contact, index) => (
              <motion.a
                key={index}
                href={contact.href}
                target="_blank"
                rel="noopener noreferrer"
                variants={item}
                className={`flex items-center p-4 bg-zinc-800/70 backdrop-blur-sm 
                  text-gray-300 rounded-lg hover:bg-zinc-700/70 transition-all duration-300 
                  border border-zinc-700/40 hover:border-zinc-600/60 group ${contact.color}`}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                <div className="mr-3 transform group-hover:scale-110 transition-transform" aria-hidden="true">
                  {React.cloneElement(contact.icon, { 'aria-hidden': 'true' })}
                </div>
                <span className="font-medium text-sm">{contact.label}</span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 backdrop-blur-sm 
            border border-blue-500/20 rounded-2xl p-8 max-w-2xl mx-auto">
            <h4 className="text-xl font-semibold text-white mb-3">
              Ready to Start a Project?
            </h4>
            <p className="text-gray-400 mb-6">
              Whether you need web development, AI solutions, or data analysis,
              I&apos;m here to help bring your ideas to life.
            </p>
            <motion.a
              href={`https://wa.me/${profile.contact.whatsapp}`}
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 
                text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 
                transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaWhatsapp className="mr-2" />
              Let&apos;s Talk
            </motion.a>
          </div>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1.0 }}
          className="mt-12 text-center text-gray-500"
        >
          <p className="text-sm">
            Available for freelance projects and full-time opportunities
          </p>
        </motion.div>
      </div>
    </section>
  );
}